// src/Admin/StudentProfile.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUserGraduate,
  faUser,
  faChartLine,
  faBookOpen,
  faWallet,
  faCalendarCheck,
  faAward,
  faHandshake,
  faPenToSquare,
  faKey,
  faCalendarWeek,
  faCalendarPlus,
  faEnvelope,
  faCakeCandles,
  faGraduationCap,
  faHashtag,
  faClock,
  faPaperPlane,
  faRotate,
  faTriangleExclamation,
  faCircleCheck,
  faXmark,
  faPlus,
  faPen,
  faTrashCan,
  faReceipt,
  faUserCheck,
  faLock,
  faListCheck,
  faArrowUpRightFromSquare as faOpenProfile,
  faCopy,
  faGlobe,
  faCheck,
  faGift,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faInstagram } from '@fortawesome/free-brands-svg-icons';
import EditStudentInfo from './EditStudentInfo';
import EditStudentInfodemo from './EditStudentInfodemo';
import ResetPassword from './ResetPassword';
import StudentSchedule from './StudentSchedule';
import StudentPaidAvailability from './StudentPaidAvailability';
import StudentSubscription from './StudentSubscription';
import StudentClasses from './StudentClasses';
import SessionBalance from './SessionBalance';
import DemoFollowUp from './DemoFollowUp';
import DemoReport from './DemoReport'; // the teacher's demo form: project, parents, rating, notes
import StudentSubmitClasses from './SubmitClasses';
import Certificates from './Certificates';
import PTM from './StudentPtm';
import UserTrackingTab from './UserTrackingTab';
import DemoTeacherAvailability from './DemoTeacherAvailability';
import { fromUtc, fmtUtc, ago } from './Servertime';
import 'flag-icons/css/flag-icons.min.css';
import { countryOf, cityOf, offsetLabel, localTimeIn, formatInZone, ianaOf } from './Studentzone';
import './Studentprofilelegacy.css'; // LEGACY: old sp-admin- styles for the tabs not rebuilt yet
import './StudentProfile.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const DEFAULT_PASSWORD = '123456aA@'; // same fallback as before

/* ---------- Tabs (same sections as before; "Statistics" now sits on the Overview) ---------- */
const TABS = [
  { key: 'profileinfo', label: 'Overview', icon: faUser },
  { key: 'followup', label: 'Follow-up', icon: faListCheck, demo: true },
  { key: 'tracking', label: 'Activity', icon: faChartLine },
  { key: 'classes', label: 'Classes', icon: faBookOpen, paid: true },
  { key: 'awaitingpayment', label: 'Payments', icon: faWallet, paid: true },
  { key: 'submitclasses', label: 'Submit', icon: faCalendarCheck, paid: true },
  { key: 'certificates', label: 'Certificates', icon: faAward, paid: true },
  { key: 'studentptm', label: 'PTM', icon: faHandshake, paid: true },
  { key: 'edit', label: 'Edit & teacher', icon: faPenToSquare },
  { key: 'reset', label: 'Reset password', icon: faKey, paid: true },
  { key: 'calendar', label: 'Schedule', icon: faCalendarWeek },
  { key: 'teachertimes', label: 'Teacher times', icon: faCalendarPlus, demo: true },
  { key: 'calendarslot', label: 'Slots', icon: faCalendarPlus, paid: true, needsTeacher: true },
];

const digits = (p) => String(p || '').replace(/\D/g, '');
const waUrl = (phone, text = '') =>
  `https://api.whatsapp.com/send/?phone=${digits(phone)}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// local YYYY-MM-DD (toISOString() used UTC and could give yesterday's date)
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// a saved payment date shown without any time-zone shift ("Oct 8, 2026")
const fmtDay = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* Same rules as before */
const financialOf = (payments) => {
  let paid = 0;
  let unpaid = 0;
  payments.forEach((p) => {
    const amount = parseFloat(p.Amount) || 0;
    if (p.Check === 1) paid += amount;
    else unpaid += amount;
  });
  return { totalPaidAmount: paid, totalUnpaidAmount: unpaid };
};

const sessionStatsOf = (schedules, payments) => {
  const now = new Date();
  const scheduled = schedules.length;
  const completed = schedules.filter((s) => s.done === 1).length;
  const upcoming = schedules.filter((s) => (!s.done || s.done === 0) && new Date(s.ClassDateTime) > now).length;
  let paidSessions = 0;
  let unpaidSessions = 0;
  payments.forEach((p) => {
    if (p.Check === 1) paidSessions += parseInt(p.SessionsCount, 10) || 0;
    if (p.Check === 0) unpaidSessions += parseInt(p.SessionsCount, 10) || 0;
  });
  return {
    remainingSessions: paidSessions + unpaidSessions - scheduled,
    scheduledSessions: scheduled,
    completedSessions: completed,
    notCompletedSessions: upcoming,
    unpaidSessions,
  };
};

const EMPTY_PAYMENT = (id) => ({
  Id: null,
  IdStudent: id,
  DateTimeOfPayement: todayStr(),
  Check: 0,
  Amount: 0,
  SessionsCount: '',
  SessionType: 'standard', // standard | mastery (Robotics = Mastery)
  ReceiptImg: null,
  existingReceiptImg: null,
});

const Photo = ({ src, flag, flagTitle }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmasp-photo-wrap">
      <span className="rmasp-photo">
        {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
      </span>
      {flag && (
        <span className="rmasp-flag" title={flagTitle}>
          <span className={`fi fi-${flag}`} />
        </span>
      )}
    </span>
  );
};

/* The student's clock, ticking every 30 seconds (only this small box re-draws) */
const StudentClock = ({ tz, hours }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return <>{localTimeIn(tz, hours, now) || '—'}</>;
};

/* WhatsApp link button (greyed out when there's no number) */
/* "Registration Update" for the parents on WhatsApp: amount, sessions, how to transfer (one payment row) */
const registrationText = (name, p) => {
  const amount = Number(p.Amount) || 0;
  const amountText = Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
  const n = Number(p.SessionsCount) || 0;
  return [
    '*Registration Update:*',
    `*Student:* ${name}`,
    `*Amount:* ${amountText}`,
    n ? `*Number of Sessions:* ${n} ${n === 1 ? 'session' : 'sessions'} ${p.SessionType === 'mastery' ? 'Mastery' : 'Standard'}` : null,
    '',
    '_You can transfer the amount via Western Union, OMT, WHISH, or BOB using the following details:_',
    '',
    '*Recipient:* Nadim Nasrallah',
    '*Recipient Number:* 03315951',
  ]
    .filter((x) => x !== null)
    .join('\n');
};

const WaButton = ({ phone, text = '', icon, label, className = 'rmasp-btn--ghost' }) =>
  digits(phone) ? (
    <a className={`rmasp-btn ${className}`} href={waUrl(phone, text)} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon icon={icon} /> {label}
    </a>
  ) : (
    <button type="button" className={`rmasp-btn ${className}`} disabled title="No mobile number">
      <FontAwesomeIcon icon={icon} /> {label}
    </button>
  );

const StudentProfile = () => {
  const { id } = useParams();
  const isDemoStudent = String(id).includes('demo'); // same rule as before
  const [params, setParams] = useSearchParams();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);

  const [teachers, setTeachers] = useState([]);
  const [assignedTeacher, setAssignedTeacher] = useState('');
  const [subStatus, setSubStatus] = useState('active'); // subscription: active / paused / stopped
  const [refCode, setRefCode] = useState(''); // family invite code (referral), paid students only
  const [challenges, setChallenges] = useState([]); // open / upcoming competitions (for the WhatsApp button)

  useEffect(() => {
    if (isDemoStudent || !id) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/admin/competitions`, auth())
      .then((res) => !cancelled && setChallenges((res.data?.competitions || []).filter((c) => c.status === 'open' || c.status === 'upcoming')))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, isDemoStudent]);

  useEffect(() => {
    if (isDemoStudent || !id) return undefined;
    let cancelled = false;
    axios
      .post(`${API}/api/admin/referrals/codes`, { ids: [id] }, auth())
      .then((res) => !cancelled && setRefCode(res.data?.codes?.[id] || ''))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, isDemoStudent]);

  const [payments, setPayments] = useState([]);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [payment, setPayment] = useState(null); // add / edit form
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [typeBal, setTypeBal] = useState(null); // Standard / Mastery sessions (Overview)
  const [refDisc, setRefDisc] = useState(null); // referral discount for the payment being added { percent, note, apply }
  const [deleting, setDeleting] = useState(null); // payment to delete
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [sessionStats, setSessionStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [tracking, setTracking] = useState({ loading: false, data: null });
  const [offsets, setOffsets] = useState([]); // time-zone list (same endpoint as the Edit tab)

  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState(false);

  // tab in the address (?tab=awaitingpayment); hidden tabs fall back to Overview
  const visibleTabs = TABS.filter((t) => (!t.paid || !isDemoStudent) && (!t.demo || isDemoStudent) && (!t.needsTeacher || assignedTeacher));
  const rawTab = params.get('tab');
  const wanted = rawTab === 'statistics' ? 'profileinfo' : rawTab === 'assignment' ? 'edit' : rawTab;
  const activeTab = visibleTabs.some((t) => t.key === wanted) ? wanted : 'profileinfo';
  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'profileinfo') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Load student (same endpoints) ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        if (isDemoStudent) {
          const res = await axios.get(`${API}/api/admin/studentprofile/demostudent/${id}`, auth());
          if (cancelled) return;
          setStudent(res.data || null);
        } else {
          const [studentRes, teachersRes, assignedRes] = await Promise.all([
            axios.get(`${API}/api/admin/studentprofile/paidstudent/${id}`, auth()),
            axios.get(`${API}/api/admin/studentprofile/teachers/list`, auth()),
            axios.get(`${API}/api/admin/studentprofile/teacherassigned/${id}`, auth()),
          ]);
          if (cancelled) return;
          setStudent(studentRes.data || null);
          setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
          setAssignedTeacher(assignedRes.data?.[0]?.IdTeacher || '');

          const paymentsRes = await axios.get(`${API}/api/admin/student-payments/${id}`, auth());
          if (cancelled) return;
          setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
          setPaymentsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.response?.data?.error || 'We couldn’t load this student.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isDemoStudent, reload]);

  // ---------- Session statistics (same endpoint + same maths) ----------
  const loadStats = useCallback(async () => {
    if (isDemoStudent) return;
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/schedule/${id}`, auth());
      setSessionStats(sessionStatsOf(Array.isArray(res.data) ? res.data : [], payments));
    } catch (err) {
      console.error('Error fetching schedule:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [id, isDemoStudent, payments]);

  // runs once payments are loaded (also when there are none) and after every payment change
  useEffect(() => {
    if (!isDemoStudent && paymentsLoaded) loadStats();
  }, [isDemoStudent, paymentsLoaded, loadStats]);

  // ---------- Activity (same endpoints), loaded when its tab opens ----------
  useEffect(() => {
    if (activeTab !== 'tracking') return undefined;
    let cancelled = false;
    setTracking({ loading: true, data: null });
    (async () => {
      try {
        const url = isDemoStudent ? `${API}/api/user-tracking/demostudent/${id}` : `${API}/api/user-tracking/student/${id}`;
        const res = await axios.get(url, auth());
        const d = res.data?.data;
        if (!cancelled) {
          setTracking({ loading: false, data: { visits: d?.visits || [], summary: d?.summary || { totalVisits: 0 } } });
        }
      } catch (err) {
        console.error('Error fetching tracking data:', err);
        if (!cancelled) setTracking({ loading: false, data: { visits: [], summary: { totalVisits: 0 } } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id, isDemoStudent]);

  // ---------- Time-zone list, for the flag + local time on top ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/studentprofile/OffsetList`, auth());
        if (!cancelled) setOffsets(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching timezones:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // Escape closes the open window
  useEffect(() => {
    if (!payment && !deleting && !convertOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!paymentSaving) setPayment(null);
      if (!deleteBusy) setDeleting(null);
      if (!converting) setConvertOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [payment, deleting, convertOpen, paymentSaving, deleteBusy, converting]);

  // ---------- WhatsApp messages (same text as before) ----------
  const credentialsText = () =>
    `Dear ${student.FirstName} ${student.LastName},\n\nWelcome to *RoboMind Academy*!\n\nYour login credentials:\n*Username:* ${student.Username}\n*Password:* ${
      student.CodeJoinDemo || DEFAULT_PASSWORD
    }\n\n*Website:* robomind-edu.com\nClick *Login* to access your dashboard.\n\nBest regards,\n*RoboMind Academy Team*`;

  // same message as before, but the class time is written in the STUDENT's time zone, with the zone named
  const demoInviteText = (tz, hours) => {
    const when = formatInZone(student.ClassDateTime, tz, hours);
    // e.g. "Fri, Oct 9, 3:00 PM Beirut time" / "… Lagos time" (UTC+x when only the hours are saved)
    const city = cityOf(tz);
    const zone = city ? ` ${city} time` : offsetLabel(tz, hours) ? ` ${offsetLabel(tz, hours)}` : '';
    return `Dear ${student.FirstName} ${student.LastName},\n\nYour *demo class* is confirmed on *${when}${zone}*.\n\n*How to join:*\n1. Go to *robomind-edu.com*\n2. Click *Join Free Class*\n3. Enter code: *${student.CodeJoinDemo}*\n\nPlease join from your *computer*.\n\nBest regards,\n*RoboMind Academy Team*`;
  };

  // Ready WhatsApp message about the referral program, with this family's link
  const referralText = () => {
    const first = String(student.FirstName || '').trim().split(' ')[0] || 'your child';
    const site = (process.env.REACT_APP_SITE_URL || 'https://robomind-edu.com').replace(/\/$/, '');
    return `Hello! 🎁 Good news from *RoboMind*: you can now invite friends and save.\n\nShare ${first}'s personal link with friends who have children aged 6 to 17:\n${site}/FreeTrial?ref=${refCode}\n\n• Your friend gets a *free class* and *10% off* their first package\n• You get *10% off your next package* for every friend who joins (up to 30%)\n\nThe link is also on ${first}'s dashboard. Thank you for trusting us! 💛`;
  };

  // The challenge for this student's level: open first, otherwise the next one
  const level = student?.IdGradesPlans === 1 ? 'little' : 'junior';
  const challenge = [...challenges]
    .filter((c) => c.level === 'all' || c.level === level)
    .sort((a, b) => (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1) || String(a.endDate).localeCompare(String(b.endDate)))[0];

  // Ready WhatsApp message about that challenge
  const competitionText = () => {
    const first = String(student.FirstName || '').trim().split(' ')[0] || 'your child';
    const site = (process.env.REACT_APP_SITE_URL || 'https://robomind-edu.com').replace(/\/$/, '');
    const nice = (d) => {
      const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
    };
    const desc = String(challenge.description || '').trim();
    const short = desc.length > 400 ? `${desc.slice(0, 400).trim()}…` : desc;
    return [
      `Hello! 🏆 A new *RoboMind challenge* ${challenge.status === 'open' ? 'is open' : `starts on ${nice(challenge.startDate)}`}: *${challenge.title}*`,
      short,
      `${first} can send the project link from the *Competitions* page on the dashboard before *${nice(challenge.endDate)}*. Our teachers score every project, and the winners are announced to all students.`,
      challenge.prize ? `🎁 Prize: ${challenge.prize}` : '',
      `Join here: ${site}/student/competitions`,
    ]
      .filter(Boolean)
      .join('\n\n');
  };

  const socialText = () => {
    const fullName = `${student.FirstName} ${student.LastName}`.trim();
    return `We usually design a post on social media to welcome our new student and we use it later to congratulate them when they pass a level. If you would like to participate, please send us a beautiful picture of ${fullName} so we can share it with you. Thank you! 😊\n\nDon't forget to follow us on Instagram: www.instagram.com/robomind.lb`;
  };

  // ---------- Demo → paid (same endpoint; the server sends the new profile address) ----------
  // ---------- Demo: send the private "feedback & enroll" link on WhatsApp ----------
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkMsg, setLinkMsg] = useState('');
  const sendEnrollLink = async () => {
    // open the tab now (a tab opened after waiting for the server is often blocked)
    const tab = window.open('', '_blank');
    setLinkBusy(true);
    setLinkMsg('');
    try {
      const res = await axios.post(`${API}/api/admin/parentfeedback/enroll-link`, { studentId: id }, auth());
      const url = waUrl(res.data.mobile || student.MobileNumber, res.data.text);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (tab) tab.close();
      setLinkMsg(err.response?.data?.error || 'Couldn’t make the link.');
    } finally {
      setLinkBusy(false);
    }
  };

  // Enroll dialog: 1) Paid or Pending  2) confirm (Yes / No) with the teacher (the demo teacher by default)
  // and the "Let's Celebrate" email to the teacher. pending = reserve only (no login email until they pay).
  const [enrollChoice, setEnrollChoice] = useState(null); // null | 'paid' | 'pending'
  const [enrollTeachers, setEnrollTeachers] = useState([]);
  const [enrollTeacher, setEnrollTeacher] = useState('');
  const [enrollCongrats, setEnrollCongrats] = useState(true);
  const [enrollCourses, setEnrollCourses] = useState({ modules: [], interestedCourse: '' }); // starting course choices
  const [enrollCourse, setEnrollCourse] = useState('');
  const openEnroll = async () => {
    setConvertError('');
    setEnrollChoice(null);
    setConvertOpen(true);
    try {
      setEnrollCourse('');
      const [list, demoT, courses] = await Promise.all([
        axios.get(`${API}/api/admin/studentprofile/teachers/list`, auth()),
        axios.get(`${API}/api/admin/studentprofile/demoteacher/${id}`, auth()).catch(() => ({ data: null })),
        axios.get(`${API}/api/admin/studentprofile/startcourse/${id}`, auth()).catch(() => ({ data: null })),
      ]);
      setEnrollCourses({ modules: courses.data?.modules || [], interestedCourse: courses.data?.interestedCourse || '' });
      const all = Array.isArray(list.data) ? list.data : [];
      setEnrollTeachers(all);
      // the teacher of the free class, selected by default (ids compared without upper / lower case)
      const demoId = String(demoT.data?.teacher?.id || demoT.data?.teacher?.IdTeacher || '').toLowerCase();
      const match = all.find((t) => String(t.id).toLowerCase() === demoId);
      setEnrollTeacher(match ? String(match.id) : '');
    } catch {
      setEnrollTeachers([]);
    }
  };
  const pickEnroll = (choice) => {
    setConvertError('');
    setEnrollChoice(choice);
    setEnrollCongrats(choice === 'paid'); // the celebration email is for a new PAID student
  };

  const convertToPaid = async (pending = false) => {
    setConverting(pending ? 'pending' : 'paid');
    setConvertError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/studentprofile/convertdemo/${id}`,
        { pending, teacherId: enrollTeacher || null, sendCongrats: !!enrollTeacher && enrollCongrats, startModuleId: enrollCourse || null },
        auth()
      );
      window.location.href = res.data;
    } catch (err) {
      setConvertError(err.response?.data?.error || err.message || 'Couldn’t update this student.');
      setConverting(false);
    }
  };

  // ---------- Payments (same endpoints, same FormData fields) ----------
  const reloadPayments = async () => {
    const res = await axios.get(`${API}/api/admin/student-payments/${id}`, auth());
    setPayments(Array.isArray(res.data) ? res.data : []);
  };

  const openAddPayment = () => {
    setPaymentError('');
    setPayment(EMPTY_PAYMENT(id));
    setRefDisc(null);
    axios
      .get(`${API}/api/admin/referrals/discount/${id}`, auth())
      .then((res) => res.data?.percent > 0 && setRefDisc({ ...res.data, apply: true }))
      .catch(() => {}); // no referral info: add the payment as usual
  };

  const openEditPayment = (p) => {
    setRefDisc(null);
    setPaymentError('');
    setPayment({
      Id: p.Id,
      IdStudent: p.IdStudent,
      DateTimeOfPayement: p.DateTimeOfPayement ? String(p.DateTimeOfPayement).split('T')[0] : '',
      Check: p.Check,
      Amount: p.Amount,
      SessionsCount: p.SessionsCount || '',
      SessionType: p.SessionType === 'mastery' ? 'mastery' : 'standard',
      ReceiptImg: null,
      existingReceiptImg: p.ReceiptImg || null,
      DiscountPercent: p.DiscountPercent || 0,
      DiscountNote: p.DiscountNote || '',
    });
  };

  const onPaymentField = (e) => {
    const { name, value, type, checked, files } = e.target;
    setPaymentError('');
    if (type === 'file') setPayment((p) => ({ ...p, [name]: files[0] || null }));
    else setPayment((p) => ({ ...p, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const savePayment = async (e) => {
    e.preventDefault();
    if (payment.Check && !payment.DateTimeOfPayement) {
      setPaymentError('Payment date is required when marking as paid');
      return;
    }
    setPaymentSaving(true);
    setPaymentError('');
    try {
      const formData = new FormData();
      formData.append('IdStudent', payment.IdStudent);
      formData.append('DateTimeOfPayement', payment.DateTimeOfPayement || '');
      formData.append('Amount', payment.Amount);
      formData.append('Check', payment.Check);
      formData.append('SessionsCount', payment.SessionsCount || '');
      formData.append('SessionType', payment.SessionType || 'standard');
      if (!payment.Id && refDisc?.apply) formData.append('applyReferral', '1'); // the server works out the discount
      if (payment.existingReceiptImg) formData.append('existingReceiptImg', payment.existingReceiptImg);
      if (payment.ReceiptImg) formData.append('receipt', payment.ReceiptImg);

      const url = payment.Id ? `${API}/api/admin/student-payments/update/${payment.Id}` : `${API}/api/admin/student-payments/add`;
      const response = await fetch(url, {
        method: payment.Id ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        body: formData,
      });
      if (!response.ok) {
        let msg = 'Failed to save payment';
        try {
          msg = (await response.json()).error || msg;
        } catch {
          /* not JSON */
        }
        throw new Error(msg);
      }
      await reloadPayments();
      setToast(payment.Id ? 'Payment updated.' : !payment.Id && refDisc?.apply ? `Payment added with ${refDisc.percent}% referral discount.` : 'Payment added.');
      setRefDisc(null);
      setPayment(null);
    } catch (err) {
      setPaymentError(err.message || 'Failed to save payment');
    } finally {
      setPaymentSaving(false);
    }
  };

  const confirmDeletePayment = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/student-payments/delete/${deleting.Id}`, auth());
      await reloadPayments();
      setDeleting(null);
      setToast('Payment deleted.');
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message || 'Couldn’t delete this payment.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const receiptUrl = (img) => (img ? `${process.env.REACT_APP_API_BASE_URL1}/assets/receipts/${img}` : null);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* clipboard blocked */
    }
  };

  /* ---------- States ---------- */
  const backTo = isDemoStudent ? '/Admin/users?tab=students&sub=demo' : '/Admin/users?tab=students';

  if (loading && !student) {
    return (
      <div className="rmasp" aria-busy="true">
        <div className="rmasp-inner">
          <div className="rmasp-skel rmasp-skel--head" />
          <div className="rmasp-skel rmasp-skel--bar" />
          <div className="rmasp-skel rmasp-skel--body" />
        </div>
      </div>
    );
  }

  if (loadError || !student) {
    return (
      <div className="rmasp">
        <div className="rmasp-inner">
          <Link className="rmasp-back" to={backTo}>
            <FontAwesomeIcon icon={faArrowLeft} /> All students
          </Link>
          <div className="rmasp-empty">
            <FontAwesomeIcon icon={faTriangleExclamation} className={loadError ? 'is-error' : ''} />
            <p>{loadError || 'Student not found.'}</p>
            {loadError && (
              <button type="button" className="rmasp-btn" onClick={() => setReload((n) => n + 1)}>
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const name = [student.FirstName, student.LastName].filter(Boolean).join(' ') || 'Student';
  const hasPhone = !!digits(student.MobileNumber);
  const lastSeen = fromUtc(student.LastSeen); // saved by Node as real UTC (sign-in / heartbeat)
  const fin = financialOf(payments);
  const accessCode = student.CodeJoinDemo || DEFAULT_PASSWORD;
  const bday = student.Birthday ? fmtDay(student.Birthday) : null;
  const tzRow = offsets.find((o) => String(o.Id) === String(student.Offset));
  const tzName = ianaOf(tzRow?.Timezone || student.Timezone || student.TimezoneName || student.timezone || '');
  const tzHours = tzRow?.TimeHours;
  const flag = countryOf(student, tzRow);
  const tzOffset = offsetLabel(tzName, tzHours);
  const hasZone = !!(tzName || tzOffset);

  return (
    <div className={`rmasp ${isDemoStudent ? 'is-demo-profile' : 'is-paid-profile'}`}>
      <div className="rmasp-inner">
        <Link className="rmasp-back" to={backTo}>
          <FontAwesomeIcon icon={faArrowLeft} /> All students
        </Link>

        {/* ---------- Header ---------- */}
        <header className="rmasp-hero">
          <span className="rmasp-kind" aria-hidden="true">{isDemoStudent ? 'Demo student' : 'Paid student'}</span>
          <Photo src={student.ImageUrl1} flag={flag} flagTitle={tzName} />
          <div className="rmasp-who">
            <span className="rmasp-eyebrow">{isDemoStudent ? 'Demo student' : 'Student'}</span>
            <h1>{name}</h1>
            <div className="rmasp-tags">
              <span className={isDemoStudent ? 'is-warn' : 'is-good'}>{isDemoStudent ? 'Demo' : 'Paid'}</span>
              <span className="is-info">{student.IdGradesPlans === 1 ? 'Little' : 'Junior'}</span>
              {!isDemoStudent && subStatus === 'paused' && <span className="is-warn">Paused</span>}
              {!isDemoStudent && subStatus === 'stopped' && <span className="is-off">Stopped</span>}
              {!isDemoStudent && subStatus === 'pending' && <span className="is-warn">Pending · not paid</span>}
            </div>
          </div>
          <dl className="rmasp-seen">
            <div className="rmasp-zone">
              <dt>
                <FontAwesomeIcon icon={faGlobe} /> Local time
              </dt>
              <dd>{hasZone ? <StudentClock tz={tzName} hours={tzHours} /> : 'No time zone'}</dd>
              {hasZone && (
                <small>
                  {flag && <span className={`fi fi-${flag}`} />} {cityOf(tzName) || 'Time zone'}
                  {tzOffset ? ` · ${tzOffset}` : ''}
                </small>
              )}
            </div>
            <div>
              <dt>
                <FontAwesomeIcon icon={faClock} /> Last seen
              </dt>
              <dd>{lastSeen ? ago(lastSeen) : 'Never'}</dd>
              {lastSeen && <small>{fmtUtc(student.LastSeen)}</small>}
            </div>
            <div>
              <dt>
                <FontAwesomeIcon icon={faUserCheck} /> Last sign-in
              </dt>
              <dd>{fmtUtc(student.Lastsignin) || 'Never'}</dd>
            </div>
          </dl>

          <div className="rmasp-actions">
            <WaButton phone={student.MobileNumber} className="rmasp-btn--wa" icon={faWhatsapp} label="WhatsApp" />
            <WaButton phone={student.MobileNumber} text={isDemoStudent ? demoInviteText(tzName, tzHours) : credentialsText()} icon={faPaperPlane} label={isDemoStudent ? 'Send demo invitation' : 'Send credentials'} />
            <WaButton phone={student.MobileNumber} text={socialText()} icon={faInstagram} label="Ask for photo" />
            {!isDemoStudent && refCode && <WaButton phone={student.MobileNumber} text={referralText()} icon={faGift} label="Referral message" />}
            {!isDemoStudent && challenge && <WaButton phone={student.MobileNumber} text={competitionText()} icon={faTrophy} label="Competition message" />}
            {isDemoStudent && (
              <button
                type="button"
                className="rmasp-btn rmasp-btn--wa"
                onClick={sendEnrollLink}
                disabled={linkBusy || !digits(student.MobileNumber)}
                title="Feedback form + choose a package, times and payment. Send it right after the free class."
              >
                <FontAwesomeIcon icon={faWhatsapp} /> {linkBusy ? 'Opening…' : 'Send feedback & enroll link'}
              </button>
            )}
            {linkMsg && <span className="rmasp-error">{linkMsg}</span>}
            {isDemoStudent && student.ConvertedTo && (
              <Link className="rmasp-btn" to={`/Admin/studentprofile/${student.ConvertedTo.id}`}>
                <FontAwesomeIcon icon={faOpenProfile} />{' '}
                {student.ConvertedTo.status === 'pending'
                  ? 'Open pending profile'
                  : student.ConvertedTo.status === 'stopped'
                  ? 'Open profile (stopped)'
                  : 'Open paid profile'}
              </Link>
            )}
            {isDemoStudent && !student.ConvertedTo && Number(student.DemoReport) === 1 && (
              <span className="rmasp-btn rmasp-btn--ghost" title="The demo report on the dashboard says Enrolled">
                <FontAwesomeIcon icon={faUserCheck} /> Enrolled
              </span>
            )}
            {isDemoStudent && !student.ConvertedTo && Number(student.DemoReport) !== 1 && (
              <button type="button" className="rmasp-btn" onClick={openEnroll}>
                <FontAwesomeIcon icon={faUserCheck} /> Enroll (paid / pending)
              </button>
            )}
          </div>
        </header>

        {/* ---------- Tabs ---------- */}
        <div className="rmasp-bar">
          <div className="rmasp-tabs" role="tablist" aria-label="Student sections">
            {visibleTabs.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} className={`rmasp-tab ${activeTab === t.key ? 'is-on' : ''}`} onClick={() => setTab(t.key)}>
                <FontAwesomeIcon icon={t.icon} />
                <span>{t.label}</span>
                {t.key === 'awaitingpayment' && payments.some((p) => !p.Check) && <i className="rmasp-dot" aria-label="has unpaid payments" />}
              </button>
            ))}
          </div>
        </div>

        {/* ---------- Content ---------- */}
        <div className="rmasp-panel" role="tabpanel">
          {activeTab === 'profileinfo' && (
            <div className="rmasp-overview">
              {/* demo student: the teacher's report; paid student: the demo they came from (if any) */}
              <DemoReport studentId={id} />
              {!isDemoStudent && (
                <>
                  <section className="rmasp-stats" aria-label="Sessions">
                    {(() => {
                      // with Standard / Mastery set up: the two kinds added up (conversions change the total)
                      const byType = typeBal?.ready && typeBal.types ? typeBal.types : null;
                      const left = byType ? byType.standard.left + byType.mastery.left : sessionStats ? sessionStats.remainingSessions : null;
                      const worst = byType ? Math.min(byType.standard.left, byType.mastery.left, left) : left;
                      const tone = left == null ? 'is-good' : worst < 0 ? 'is-bad' : left <= 2 ? 'is-warn' : 'is-good';
                      return (
                        <div className={`rmasp-stat rmasp-stat--big ${tone}`}>
                          <span>Sessions left to schedule</span>
                          <strong>{statsLoading && !sessionStats ? '…' : left ?? '—'}</strong>
                          {byType && (
                            <small className="rmasp-bytype">
                              <b className={byType.standard.left < 0 ? 'is-neg' : ''}>{byType.standard.left} Standard</b> ·{' '}
                              <b className={`is-mastery ${byType.mastery.left < 0 ? 'is-neg' : ''}`}>{byType.mastery.left} Mastery</b>
                            </small>
                          )}
                          {left != null && worst < 0 && <em>{left < 0 ? 'Overbooked' : 'Mastery / Standard not paid'}</em>}
                          <button type="button" className="rmasp-refresh" onClick={loadStats} disabled={statsLoading} aria-label="Refresh session numbers">
                            <FontAwesomeIcon icon={faRotate} spin={statsLoading} />
                          </button>
                        </div>
                      );
                    })()}
                    {[
                      ['Scheduled', 'scheduledSessions', 'is-info'],
                      ['Completed', 'completedSessions', 'is-good'],
                      ['Upcoming', 'notCompletedSessions', 'is-warn'],
                      ['Unpaid sessions', 'unpaidSessions', 'is-bad'],
                    ].map(([label, key, tone]) => (
                      <div key={key} className={`rmasp-stat ${tone}`}>
                        <span>{label}</span>
                        <strong>{sessionStats ? sessionStats[key] : '—'}</strong>
                      </div>
                    ))}
                    <div className="rmasp-stat is-good">
                      <span>Total paid</span>
                      <strong>{money(fin.totalPaidAmount)}</strong>
                    </div>
                    <div className={`rmasp-stat ${fin.totalUnpaidAmount > 0 ? 'is-bad' : 'is-off'}`}>
                      <span>Total unpaid</span>
                      <strong>{money(fin.totalUnpaidAmount)}</strong>
                    </div>
                  </section>
                  <SessionBalance studentId={id} reloadKey={sessionStats} compact onOpenPayments={() => setTab('awaitingpayment')} onLoad={setTypeBal} />
                  <StudentSubscription studentId={id} onStatus={setSubStatus} onOpenSchedule={() => setTab('calendar')} />
                </>
              )}

              <section className="rmasp-card">
                <h2 className="rmasp-h2">
                  <FontAwesomeIcon icon={faUser} /> Student information
                </h2>
                <ul className="rmasp-facts">
                  <li>
                    <FontAwesomeIcon icon={faEnvelope} />
                    <div>
                      <small>Parent email</small>
                      {student.ParentsEmail ? <a href={`mailto:${student.ParentsEmail}`}>{student.ParentsEmail}</a> : <span className="rmasp-dim">Not set</span>}
                    </div>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faWhatsapp} className="is-wa" />
                    <div>
                      <small>Mobile</small>
                      {hasPhone ? (
                        <a href={waUrl(student.MobileNumber)} target="_blank" rel="noopener noreferrer">
                          {student.MobileNumber}
                        </a>
                      ) : (
                        <span className="rmasp-dim">Not set</span>
                      )}
                    </div>
                  </li>
                  {!isDemoStudent && (
                    <li>
                      <FontAwesomeIcon icon={faUser} />
                      <div>
                        <small>Username</small>
                        <span>{student.Username || <span className="rmasp-dim">Not set</span>}</span>
                      </div>
                    </li>
                  )}
                  <li>
                    <FontAwesomeIcon icon={faKey} />
                    <div>
                      <small>{isDemoStudent ? 'Demo join code' : 'Access code'}</small>
                      <span className="rmasp-code">
                        <code>{accessCode}</code>
                        <button type="button" onClick={() => copy(accessCode)} aria-label="Copy code">
                          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                        </button>
                      </span>
                    </div>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCakeCandles} />
                    <div>
                      <small>Birthday</small>
                      <span>{bday || <span className="rmasp-dim">Not set</span>}</span>
                    </div>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faHashtag} />
                    <div>
                      <small>Age</small>
                      <span>{student.Age != null ? `${student.Age} years` : <span className="rmasp-dim">Not set</span>}</span>
                    </div>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faGraduationCap} />
                    <div>
                      <small>Grade</small>
                      <span>{student.Grade != null ? `Grade ${student.Grade}` : <span className="rmasp-dim">Not set</span>}</span>
                    </div>
                  </li>
                  {isDemoStudent && student.ClassDateTime && (
                    <li>
                      <FontAwesomeIcon icon={faCalendarWeek} />
                      <div>
                        <small>Demo class</small>
                        <span>
                          {new Date(student.ClassDateTime).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {hasZone && <em className="rmasp-dim"> · {formatInZone(student.ClassDateTime, tzName, tzHours, { hour: 'numeric', minute: '2-digit' })} {cityOf(tzName) || 'their time'}</em>}
                        </span>
                      </div>
                    </li>
                  )}
                </ul>
              </section>
            </div>
          )}

          {activeTab === 'followup' && isDemoStudent && <DemoFollowUp studentId={id} />}
          {activeTab === 'tracking' && <UserTrackingTab data={tracking.data} loading={tracking.loading} userType={isDemoStudent ? 'demostudent' : 'student'} />}

          {activeTab === 'classes' && <StudentClasses StudentId={id} />}

          {activeTab === 'awaitingpayment' && (
            <section>
              <div className="rmasp-sectionhead">
                <div>
                  <h2 className="rmasp-h2">
                    <FontAwesomeIcon icon={faWallet} /> Payments
                  </h2>
                  <p>
                    <b className="is-good-text">{money(fin.totalPaidAmount)}</b> paid · <b className={fin.totalUnpaidAmount > 0 ? 'is-bad-text' : ''}>{money(fin.totalUnpaidAmount)}</b> pending
                  </p>
                </div>
                <button type="button" className="rmasp-btn" onClick={openAddPayment}>
                  <FontAwesomeIcon icon={faPlus} /> Add payment
                </button>
              </div>

              <SessionBalance studentId={id} reloadKey={payments} />

              {payments.length === 0 ? (
                <div className="rmasp-empty">
                  <FontAwesomeIcon icon={faReceipt} />
                  <p>No payment records yet.</p>
                </div>
              ) : (
                <ul className="rmasp-pays">
                  {payments.map((p) => (
                    <li key={p.Id} className={p.Check ? 'is-paid' : 'is-pending'}>
                      <div className="rmasp-pay-main">
                        <strong>{money(p.Amount)}</strong>
                        <span>
                          {p.SessionsCount ? `${p.SessionsCount} ${p.SessionType === 'mastery' ? 'Mastery' : 'Standard'} sessions` : 'No session count'}
                          {p.SessionsCount > 0 && Number(p.Amount) > 0 ? ` (${money(Number(p.Amount) / p.SessionsCount)} each)` : ''} ·{' '}
                          {fmtDay(p.DateTimeOfPayement) || 'No date'}
                        </span>
                        {p.DiscountPercent > 0 && (
                          <span className="rmasp-pay-disc" title={p.DiscountNote || ''}>
                            <FontAwesomeIcon icon={faGift} /> −{p.DiscountPercent}% referral{p.OriginalAmount ? ` · was ${money(p.OriginalAmount)}` : ''}
                          </span>
                        )}
                      </div>
                      <span className={`rmasp-pill ${p.Check ? 'is-good' : 'is-warn'}`}>{p.Check ? 'Paid' : 'Pending'}</span>
                      <div className="rmasp-pay-actions">
                        {digits(student.MobileNumber) ? (
                          <a
                            className="rmasp-icon is-wa"
                            href={waUrl(student.MobileNumber, registrationText([student.FirstName, student.LastName].filter(Boolean).join(' '), p))}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Send the registration update on WhatsApp"
                            title="Send the registration update on WhatsApp (amount, sessions, how to transfer)"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </a>
                        ) : (
                          <span className="rmasp-icon is-empty" title="No mobile number">
                            <FontAwesomeIcon icon={faWhatsapp} />
                          </span>
                        )}
                        {p.ReceiptImg ? (
                          <a className="rmasp-icon" href={receiptUrl(p.ReceiptImg)} target="_blank" rel="noopener noreferrer" aria-label="View receipt" title="View receipt">
                            <FontAwesomeIcon icon={faReceipt} />
                          </a>
                        ) : (
                          <span className="rmasp-icon is-empty" title="No receipt">
                            <FontAwesomeIcon icon={faReceipt} />
                          </span>
                        )}
                        <button type="button" className="rmasp-icon" onClick={() => openEditPayment(p)} aria-label="Edit payment">
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button type="button" className="rmasp-icon is-danger" onClick={() => { setDeleteError(''); setDeleting(p); }} aria-label="Delete payment">
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {activeTab === 'edit' && !isDemoStudent && (
            <EditStudentInfo
              student={student}
              setStudent={setStudent}
              teachers={teachers}
              assignedTeacher={assignedTeacher}
              onTeacherAssigned={(tid) => {
                setAssignedTeacher(tid);
                setToast('Teacher assigned.');
              }}
            />
          )}
          {activeTab === 'edit' && isDemoStudent && <EditStudentInfodemo student={student} setStudent={setStudent} />}
          {activeTab === 'reset' && <ResetPassword teacherId={id} />}
          {activeTab === 'calendar' && <StudentSchedule studentId={id} studentType={isDemoStudent} assignedTeacher={assignedTeacher} />}
          {activeTab === 'teachertimes' && isDemoStudent && <DemoTeacherAvailability studentId={id} tzName={tzName} tzHours={tzHours} hasZone={hasZone} />}
          {activeTab === 'calendarslot' && <StudentPaidAvailability studentId={id} assignedTeacher={assignedTeacher} />}
          {activeTab === 'submitclasses' && <StudentSubmitClasses studentId={id} />}
          {activeTab === 'certificates' && <Certificates studentId={id} studentType={student.IdGradesPlans} studentName={[student.FirstName, student.LastName].filter(Boolean).join(' ')} />}
          {activeTab === 'studentptm' && <PTM studentId={id} studentType={student.IdGradesPlans} />}
        </div>
      </div>

      {/* ---------- Add / edit payment ---------- */}
      {payment && (
        <div className="rmasp-overlay" onClick={() => !paymentSaving && setPayment(null)}>
          <form className="rmasp-modal" role="dialog" aria-modal="true" aria-label="Payment" onClick={(e) => e.stopPropagation()} onSubmit={savePayment}>
            <header className="rmasp-modal-head">
              <h2>{payment.Id ? 'Edit payment' : 'Add payment'}</h2>
              <button type="button" className="rmasp-x" onClick={() => setPayment(null)} disabled={paymentSaving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmasp-modal-body">
              <div className="rmasp-two">
                <label className="rmasp-field">
                  <span>{!payment.Id && refDisc?.apply ? 'Package price, before discount (USD)' : 'Amount (USD)'}</span>
                  <input type="number" name="Amount" value={payment.Amount} onChange={onPaymentField} required min="0" step="0.01" inputMode="decimal" disabled={paymentSaving} />
                </label>
                <label className="rmasp-field">
                  <span>Number of sessions</span>
                  <input type="number" name="SessionsCount" value={payment.SessionsCount} onChange={onPaymentField} min="1" step="1" placeholder="e.g. 12" disabled={paymentSaving} />
                </label>
              </div>
              <div className="rmasp-field">
                <span>Sessions for</span>
                <div className="rmasp-seg-type" role="radiogroup" aria-label="Sessions for">
                  {[
                    ['standard', 'Standard courses'],
                    ['mastery', 'Mastery & Robotics'],
                  ].map(([k, l]) => (
                    <label key={k} className={(payment.SessionType || 'standard') === k ? 'is-on' : ''}>
                      <input
                        type="radio"
                        name="SessionType"
                        value={k}
                        checked={(payment.SessionType || 'standard') === k}
                        onChange={onPaymentField}
                        disabled={paymentSaving}
                      />
                      {l}
                    </label>
                  ))}
                </div>
                {Number(payment.Amount) > 0 && Number(payment.SessionsCount) > 0 && (
                  <small className="rmasp-perprice">
                    {money(
                      (!payment.Id && refDisc?.apply ? Number(payment.Amount) * (1 - refDisc.percent / 100) : Number(payment.Amount)) /
                        Number(payment.SessionsCount)
                    )}{' '}
                    per session · used as the Session fees when you approve their {payment.SessionType === 'mastery' ? 'Mastery' : 'Standard'} classes
                  </small>
                )}
              </div>
              {!payment.Id && refDisc && (
                <div className={`rmasp-refdisc ${refDisc.apply ? 'is-on' : ''}`}>
                  <label>
                    <input type="checkbox" checked={refDisc.apply} onChange={(e) => setRefDisc((r) => ({ ...r, apply: e.target.checked }))} disabled={paymentSaving} />
                    <span>
                      <b>
                        <FontAwesomeIcon icon={faGift} /> Apply referral discount −{refDisc.percent}%
                      </b>
                      <small>{refDisc.note}{refDisc.waiting > 0 ? ` · ${refDisc.waiting} more waiting for the next package` : ''}</small>
                    </span>
                  </label>
                  {refDisc.apply && Number(payment.Amount) > 0 && (
                    <p>
                      {money(payment.Amount)} − {refDisc.percent}% = <b>{money(Math.round(Number(payment.Amount) * (100 - refDisc.percent)) / 100)}</b> to pay
                    </p>
                  )}
                </div>
              )}
              {payment.Id && payment.DiscountPercent > 0 && (
                <p className="rmasp-refdisc-note">
                  <FontAwesomeIcon icon={faGift} /> {payment.DiscountNote || `${payment.DiscountPercent}% referral discount`} (already applied)
                </p>
              )}
              <label className="rmasp-field">
                <span>Payment date{payment.Check ? ' *' : ''}</span>
                <input type="date" name="DateTimeOfPayement" value={payment.DateTimeOfPayement} onChange={onPaymentField} required={!!payment.Check} disabled={paymentSaving} />
              </label>
              <div className="rmasp-field">
                <span>Receipt</span>
                {payment.existingReceiptImg && (
                  <a className="rmasp-receipt" href={receiptUrl(payment.existingReceiptImg)} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faReceipt} /> View current receipt
                  </a>
                )}
                <input type="file" name="ReceiptImg" onChange={onPaymentField} accept="image/*,application/pdf" disabled={paymentSaving} />
                <small>Max 5MB · JPEG, PNG, GIF or PDF{payment.existingReceiptImg ? ' · a new file replaces the current one' : ''}</small>
              </div>
              <label className={`rmasp-switch ${payment.Check === 1 ? 'is-on' : ''}`}>
                <input type="checkbox" name="Check" checked={payment.Check === 1} onChange={onPaymentField} disabled={paymentSaving} />
                <i aria-hidden="true" />
                <span>
                  <b>Payment received</b>
                  <small>{payment.Check === 1 ? 'Counted as paid' : 'Still pending'}</small>
                </span>
              </label>
              {paymentError && <p className="rmasp-error">{paymentError}</p>}
            </div>
            <footer className="rmasp-modal-foot">
              <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setPayment(null)} disabled={paymentSaving}>
                Cancel
              </button>
              <button type="submit" className="rmasp-btn" disabled={paymentSaving}>
                {paymentSaving ? 'Saving…' : 'Save'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* ---------- Delete payment ---------- */}
      {deleting && (
        <div className="rmasp-overlay" onClick={() => !deleteBusy && setDeleting(null)}>
          <div className="rmasp-dialog is-bad" role="alertdialog" aria-modal="true" aria-label="Delete payment" onClick={(e) => e.stopPropagation()}>
            <span className="rmasp-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2>Delete this payment?</h2>
            <p>
              <b>{money(deleting.Amount)}</b>
              {deleting.SessionsCount ? ` · ${deleting.SessionsCount} sessions` : ''} · {fmtDay(deleting.DateTimeOfPayement) || 'no date'}
            </p>
            {deleteError && <p className="rmasp-error">{deleteError}</p>}
            <div className="rmasp-dialog-actions">
              <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setDeleting(null)} disabled={deleteBusy} autoFocus>
                Cancel
              </button>
              <button type="button" className="rmasp-btn rmasp-btn--danger" onClick={confirmDeletePayment} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Demo → paid ---------- */}
      {convertOpen && (
        <div className="rmasp-overlay" onClick={() => !converting && setConvertOpen(false)}>
          <div className="rmasp-dialog rmasp-dialog--enroll is-good" role="alertdialog" aria-modal="true" aria-label="Enroll student" onClick={(e) => e.stopPropagation()}>
            <span className="rmasp-dialog-icon">
              <FontAwesomeIcon icon={faUserCheck} />
            </span>
            {!enrollChoice ? (
              <>
                <h2>Enroll {student.FirstName || 'this student'}?</h2>
                <p>Did the family pay, or did they only choose their slots?</p>
                <div className="rmasp-enroll">
                  <button type="button" className="rmasp-enroll-opt is-paid" onClick={() => pickEnroll('paid')}>
                    <FontAwesomeIcon icon={faUserCheck} />
                    <span>
                      <b>Paid</b>
                      <small>Moves to the paid students, the parents get the username and password by email. Demo report: Enrolled.</small>
                    </span>
                  </button>
                  <button type="button" className="rmasp-enroll-opt is-pending" onClick={() => pickEnroll('pending')}>
                    <FontAwesomeIcon icon={faLock} />
                    <span>
                      <b>Pending (not paid yet)</b>
                      <small>Reserves a teacher and weekly slots for 10 days. No login email, not counted as enrolled. Demo report: Pending.</small>
                    </span>
                  </button>
                </div>
                <div className="rmasp-dialog-actions">
                  <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setConvertOpen(false)} autoFocus>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>
                  {enrollChoice === 'paid'
                    ? `Make ${student.FirstName || 'this student'} a paid student?`
                    : `Reserve ${student.FirstName || 'this student'} as pending?`}
                </h2>
                <p>
                  {enrollChoice === 'paid'
                    ? 'The parents get the username and password by email.'
                    : 'No login email yet. The slots you give them are held for 10 days.'}
                </p>
                <div className="rmasp-enroll-form">
                  <label>
                    <span>Teacher</span>
                    <select value={enrollTeacher} onChange={(e) => setEnrollTeacher(e.target.value)} disabled={!!converting}>
                      <option value="">No teacher yet</option>
                      {enrollTeachers.map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <small>The teacher of the free class is chosen for you. Change it if you want.</small>
                  </label>
                  <label>
                    <span>Starting course</span>
                    <select value={enrollCourse} onChange={(e) => setEnrollCourse(e.target.value)} disabled={!!converting}>
                      <option value="">Not chosen: the teacher decides with the student</option>
                      {enrollCourses.modules.map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    <small>
                      {enrollCourses.interestedCourse
                        ? `Asked for at the free trial: ${enrollCourses.interestedCourse}.`
                        : 'If the family told you which course to start with, choose it here.'}
                    </small>
                  </label>
                  <label className="rmasp-enroll-check">
                    <input
                      type="checkbox"
                      checked={!!enrollTeacher && enrollCongrats}
                      onChange={(e) => setEnrollCongrats(e.target.checked)}
                      disabled={!enrollTeacher || !!converting}
                    />
                    <span>Send the “Let’s Celebrate” email to the teacher</span>
                  </label>
                </div>
            {convertError && <p className="rmasp-error">{convertError}</p>}
                <div className="rmasp-dialog-actions">
                  <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setEnrollChoice(null)} disabled={!!converting}>
                    No
                  </button>
                  <button type="button" className="rmasp-btn" onClick={() => convertToPaid(enrollChoice === 'pending')} disabled={!!converting} autoFocus>
                    {converting ? (enrollChoice === 'paid' ? 'Updating…' : 'Reserving…') : 'Yes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="rmasp-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default StudentProfile;